import React, { useState, useRef, useEffect } from 'react';
import { DayPicker, DateRange } from 'react-day-picker';
import { format } from 'date-fns';

type DateRangeFieldProps = {
  from: Date | null;
  to: Date | null;
  onFromChange: (date: Date | null) => void;
  onToChange: (date: Date | null) => void;
};

function DateInput({
  label,
  selectedDate,
  onChange,
  disabledBefore,
  disabledAfter,
  isFromInput,
  rangeStart,
  rangeEnd,
}: {
  label: string;
  selectedDate: Date | null;
  onChange: (date: Date | null) => void;
  disabledBefore?: Date;
  disabledAfter?: Date;
  isFromInput: boolean;
  rangeStart: Date | null;
  rangeEnd: Date | null;
}) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [month, setMonth] = useState<Date>(selectedDate ?? new Date());
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const toggleCalendar = () => {
    setShowCalendar((prev) => {
      const next = !prev;
      if (next) {
        setMonth(selectedDate ?? new Date());
      }
      return next;
    });
  };

  const handleDateSelect = (range: DateRange | undefined) => {
    if (isFromInput) {
      if (range?.from) {
        onChange(range.from);
      } else {
        onChange(null);
      }
    } else {
      if (range?.to) {
        onChange(range.to);
      } else {
        onChange(null);
      }
    }
    setShowCalendar(false);
  };

  useEffect(() => {
    if (!showCalendar) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCalendar]);

  const range = rangeStart || rangeEnd ? { from: rangeStart ?? undefined, to: rangeEnd ?? undefined } : undefined;

  return (
    <div className="date-input-wrapper" ref={wrapperRef}>
      <input
        type="text"
        ref={inputRef}
        value={selectedDate ? format(selectedDate, 'MMMM d, yyyy') : ''}
        placeholder={label}
        onClick={toggleCalendar}
        readOnly
        className={`date-input${showCalendar ? ' is-open' : ''}`}
      />
      {showCalendar && (
        <div
          className="calendar-popup"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <DayPicker
            mode="range"
            month={month}
            onMonthChange={setMonth}
            selected={range}
            onSelect={handleDateSelect}
            disabled={{
              before: disabledBefore,
              after: disabledAfter,
            }}
            className="ub-day-picker"
            navLayout="around"
          />
        </div>
      )}
    </div>
  );
}

export default function DateRangeField({
  from,
  to,
  onFromChange,
  onToChange,
}: DateRangeFieldProps) {
  const today = new Date();

  return (
    <div className="date-range-container">
      <DateInput
        label="From"
        selectedDate={from}
        onChange={onFromChange}
        disabledAfter={to ?? today}
        isFromInput={true}
        rangeStart={from}
        rangeEnd={to}
      />
      <span className="date-separator">-</span>
      <DateInput
        label="To"
        selectedDate={to}
        onChange={onToChange}
        disabledBefore={from ?? undefined}
        disabledAfter={today}
        isFromInput={false}
        rangeStart={from}
        rangeEnd={to}
      />
    </div>
  );
}
